import React, { useState, useEffect, useMemo } from "react";
import Modal from "../../components/Modal";
import SidebarLayout from "../../components/SidebarLayout";
import { BACKEND_URL } from "../../config";
import PrintBill from "./PrintBill"; // import your PrintBill component
import { formatPatientName } from "../../utils/patientName";

interface Bill {
  id: number;
  patient_id: number;
  date: string;
  totalAmount: number;
  patient: any;
  Patient?: any;
}

const BillingList: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [bills, setBills] = useState<Bill[]>([]);
  const [selectedBillId, setSelectedBillId] = useState<number | null>(null);
  const [autoPrint, setAutoPrint] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [error, setError] = useState("");

  /* ------------------ Load Bills ------------------ */
  const loadBills = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/billing/`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load bills");
      setBills(await res.json());
    } catch (err) {
      console.error("Failed to load bills:", err);
      setError("Unable to load billing records. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBills();
  }, []);

  /* ------------------ View / Print ------------------ */
  const openView = (billId: number, shouldPrint = false) => {
    setSelectedBillId(billId);
    setAutoPrint(shouldPrint);
    setViewOpen(true);
  };

  const getPatientName = (bill: Bill) => {
    const patient = bill.patient || bill.Patient;
    return formatPatientName(patient?.contact || patient?.Contact, "Unknown patient");
  };

  const invalidDateRange = Boolean(dateFrom && dateTo && dateFrom > dateTo);
  const filteredBills = useMemo(() => {
    const query = search.trim().toLowerCase();
    return bills.filter((bill) => {
      const patient = bill.patient || bill.Patient;
      const contact = patient?.contact || patient?.Contact;
      const matchesSearch = !query || [String(bill.id), formatPatientName(contact, "Unknown patient"), contact?.phone || ""]
        .some((value) => value.toLowerCase().includes(query));
      const date = new Date(bill.date);
      const localDate = Number.isNaN(date.getTime()) ? "" : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      return matchesSearch && !invalidDateRange
        && (!dateFrom || localDate >= dateFrom)
        && (!dateTo || Boolean(localDate && localDate <= dateTo));
    });
  }, [bills, search, dateFrom, dateTo, invalidDateRange]);
  const totalPages = Math.max(1, Math.ceil(filteredBills.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const visibleBills = filteredBills.slice(startIndex, startIndex + pageSize);

  /* ------------------ UI ------------------ */
  return (
    <SidebarLayout title="Billing Records">
      <div className="bg-surface-light p-6 rounded border print:hidden">
        <h2 className="text-xl font-semibold mb-4">All Bills</h2>

        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          <label className="text-sm">
            <span className="block mb-1 font-medium">Search bills</span>
            <input type="search" placeholder="Bill ID, patient name or phone" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full p-2 border rounded" />
          </label>
          <label className="text-sm">
            <span className="block mb-1 font-medium">From date</span>
            <input type="date" value={dateFrom} max={dateTo || undefined}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="w-full p-2 border rounded" />
          </label>
          <label className="text-sm">
            <span className="block mb-1 font-medium">To date</span>
            <input type="date" value={dateTo} min={dateFrom || undefined}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="w-full p-2 border rounded" />
          </label>
          <div className="flex items-end">
            <button type="button" className="px-3 py-2 border rounded"
              onClick={() => { setSearch(""); setDateFrom(""); setDateTo(""); setPage(1); }}>Clear filters</button>
          </div>
        </div>
        {invalidDateRange && <p role="alert" className="mb-3 text-red-600">From date must be on or before To date.</p>}

        {loading ? (
          <div>Loading...</div>
        ) : error ? (
          <div role="alert">{error} <button type="button" onClick={loadBills} className="underline">Retry</button></div>
        ) : filteredBills.length === 0 ? (
          <div className="text-gray-500">{bills.length ? "No bills match your search filters." : "No billing records found."}</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[600px] border">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 border">Bill ID</th>
                <th className="p-2 border">Patient</th>
                <th className="p-2 border">Date</th>
                <th className="p-2 border">Total</th>
                <th className="p-2 border">Actions</th>
              </tr>
            </thead>

            <tbody>
              {visibleBills.map((b) => (
                <tr key={b.id}>
                  <td className="p-2 border">{b.id}</td>
                  <td className="p-2 border">{getPatientName(b)}</td>
                  <td className="p-2 border">{new Date(b.date).toLocaleDateString("en-GB")}</td>
                  <td className="p-2 border">₹{b.totalAmount}</td>
                  <td className="p-2 border">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openView(b.id)}
                        className="px-3 py-1 rounded bg-blue-600 text-white"
                      >
                        View
                      </button>
                      <button
                        onClick={() => openView(b.id, true)}
                        className="px-3 py-1 rounded bg-green-600 text-white"
                      >
                        Print
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        {!loading && !error && (
          <nav aria-label="Billing pagination" className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
            <div aria-live="polite">Showing {filteredBills.length ? startIndex + 1 : 0}–{Math.min(startIndex + pageSize, filteredBills.length)} of {filteredBills.length} bills</div>
            <label className="flex items-center gap-2">Bills per page
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="p-2 border rounded">
                {[10, 25, 50].map((size) => <option key={size} value={size}>{size}</option>)}
              </select>
            </label>
            <div className="flex items-center gap-3">
              <button type="button" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} className="px-3 py-2 border rounded disabled:opacity-50">Previous</button>
              <span>Page {currentPage} of {totalPages}</span>
              <button type="button" disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)} className="px-3 py-2 border rounded disabled:opacity-50">Next</button>
            </div>
          </nav>
        )}
      </div>

      {/* ------------------ View / Print Modal ------------------ */}
      <Modal
        open={viewOpen}
        title={``}
        onClose={() => setViewOpen(false)}
        fullScreen={true}
      >
        <div className="w-full">
          {selectedBillId && <PrintBill key={selectedBillId} billId={selectedBillId} autoPrint={autoPrint} />}
        </div>
      </Modal>
    </SidebarLayout>
  );
};

export default BillingList;
